/**
 * ll97Process — tolerant codecs for the LL97 PM process-view surface.
 *
 * Models the exact same transport-agnostic, additive-contract idiom as
 * ``api/filingContext.ts``: every parser reads its fields through
 * ``dualKey`` (snake_case first — the BE / ``fields="__all__"`` truth —
 * camelCase fall-back for payloads that rode the legacy axios
 * camelizer), and returns ``null`` / ``[]`` rather than throwing when a
 * required core field is absent. So the SAME parser handles a payload
 * off the camelizing interceptor OR a future raw ``codecGet`` path.
 *
 * The process view consumes a small, declared slice of the LL97
 * submission serializer's ~40 ``__all__`` + depth-1 fields; every
 * undeclared field (including the ``audit_trail`` serializer-leak fields
 * ``internal_description`` / ``extra_data``, stripped explicitly in
 * ``parseLl97AuditTrail``) is simply dropped.
 *
 * FRONTEND-ONLY: reads three already-authorized, company-scoped
 * endpoints a portal PM can hit today — ``GET /audits/ll97/<id>/``,
 * ``GET /audits/ll97/<id>/audit_trail/``, ``GET /audits/rdps/``.
 */
import { dualKey, isObject } from "@/api/codec";
import { filingContextSchema, type FilingContext } from "@/api/filingContext";
import api from "@/api/base";

// ---------------------------------------------------------------------------
// Wire constants — verified against `audit/models/LL97.py` (LL97SubmissionStatus).
// The status chip renders the enum NAME token (``ECM_FILES``), not the label.
// ---------------------------------------------------------------------------

export const LL97_STATUS_TOKEN: Record<number, string> = {
  0: "NOT_STARTED",
  100: "ADD_BIN",
  200: "CONNECT_ESPM",
  210: "VERIFY_ESPM",
  310: "ARTICLE_320_COMPLIANT_QUESTIONNAIRE",
  311: "ARTICLE_320_NONCOMPLIANT_QUESTIONNAIRE",
  320: "ARTICLE_321_PERFORMANCE_QUESTIONNAIRE",
  321: "ARTICLE_321_PRESCRIPTIVE_QUESTIONNAIRE",
  350: "PORTFOLIO_MANAGER_QUESTIONNAIRE",
  400: "UPLOAD_FILES",
  422: "DECARBONIZATION_PLAN_FILES_UPLOAD",
  423: "SITE_VISIT_FILES_UPLOAD_320_GFE",
  434: "GFA_FILES",
  441: "ECM_QUESTIONNAIRE",
  444: "ECM_FILES",
  460: "OWNER_ATTESTATION",
  550: "RDP_ASSIGNMENT",
  700: "VERIFICATION",
  800: "RDP_REVIEW",
  850: "SUBMISSION_IN_PROGRESS",
  900: "SUBMITTED",
  910: "MISSING_PECMS",
  911: "MISSING_PECMS_LL88_ONLY",
  912: "RESPONDED_TO_MISSING_PECMS",
  915: "DEFICIENT_RESPONSE_REQUESTED",
  916: "DEFICIENT_RESPONDED",
  1000: "ACCEPTED",
  1100: "REJECTED",
  9001: "EXEMPT_CP1",
  9002: "EXEMPT_CP2",
  9003: "EXEMPT_CP4",
  9004: "EXEMPT_NO_CBL",
  9005: "EXEMPT_CP_TRANSITION",
};

/** Terminal-success status — the cycle is closed (accepted). */
export const LL97_STATUS_ACCEPTED = 1000;

// ---------------------------------------------------------------------------
// FR-2 — the `LawSubmissionMessage` correspondence wire (comms primitive).
//
// The LL97 process-view (S5) + the CBL-dispute `AdditionalInformation` thread
// consume the SAME `LawSubmissionMessageSerializer` shape the comms tri-party
// thread does (`author_party` / `author_display` / `body` / `created_at` /
// `visibility`[staff wire only] / `extra_data`[key-whitelisted] — camelized to
// `authorParty`/`authorDisplay`/`createdAt`/`extraData`). No second wire idiom:
// the shape is byte-identical to `Flow/adapterTypes.ts`'s `Ll87Message`, pinned
// by the parity test in `__tests__/ll97Messages.test.ts`. Only the RENDER forks
// (S5 frame-06 vs the dispute's shadcn surface) — never the data.
// ---------------------------------------------------------------------------

/** The five author parties (`LawSubmissionMessage.PARTY_*`). Opaque VALUES we
 *  MAP, never parse — an unknown token fails CLOSED to a neutral incoming card. */
export const LL97_MESSAGE_PARTIES = [
  "owner",
  "carbonclear",
  "rdp",
  "dob",
  "system",
] as const;

export type Ll97MessageParty = (typeof LL97_MESSAGE_PARTIES)[number];

export const isLl97MessageParty = (v: unknown): v is Ll97MessageParty =>
  typeof v === "string" &&
  (LL97_MESSAGE_PARTIES as readonly string[]).includes(v);

/**
 * Bubble side (FR-2 §What): owner + CarbonClear render self-end (our side);
 * DOB (and any incoming/unknown party) renders self-start. Shared by BOTH the
 * S5 frame-06 thread and the dispute shadcn thread so the party→side mapping is
 * defined ONCE (no fork of the render decision).
 */
export type Ll97MessageSide = "self" | "incoming";

export const ll97MessageSide = (party: string): Ll97MessageSide =>
  party === "owner" || party === "carbonclear" ? "self" : "incoming";

/**
 * The whitelisted `extra_data` surface (camelized). Mirrors the BE
 * `_MESSAGE_EXTRA_DATA_WHITELIST` (`serializers/LawSubmission.py:22`): nothing
 * else reaches the non-staff wire. `receivedAt` is declared for the FR-6 14-day
 * anchor — it is NOT whitelisted today (so it tolerant-parses to `null` and the
 * objection copy honestly renders WITHOUT a days-remaining figure), and picks up
 * the real inbound provenance for free if/when FR-7's decision-ingest whitelists
 * it. Nothing here is a debt/overclaim surface.
 */
export interface Ll97MessageExtraData {
  handoff?: string | null;
  routedRecipients?: string[] | null;
  statusFrom?: string | null;
  statusTo?: string | null;
  /** Inbound DOB email `received_at` — the ONLY honest 14-day anchor (FR-6). */
  receivedAt?: string | null;
}

export interface Ll97Message {
  id: string;
  /** {owner,carbonclear,rdp,dob,system} — opaque VALUE (map, never parse). */
  authorParty: string;
  /** Render VERBATIM (server truth): owner name / "CarbonClear" / "DOB". */
  authorDisplay: string;
  body: string;
  createdAt: string | null;
  /** Staff wire only (`owner_visible` | `staff_internal`) — absent for owners. */
  visibility?: string | null;
  extraData?: Ll97MessageExtraData | null;
}

/**
 * `NycLLAttestationStatus` ladder (`audit/models/RDP.py`). The demo
 * honesty invariant: the process view NEVER renders past OWNER_SIGNED
 * (2) on a live cycle — the RDP seal is not simulated.
 */
export const LL97_ATTESTATION = {
  BLANK: 0,
  RDP_INFO_FILLED: 1,
  OWNER_SIGNED: 2,
  RDP_SIGNED: 3,
  COMPLETE: 4,
} as const;

// ---------------------------------------------------------------------------
// Interfaces
// ---------------------------------------------------------------------------

export interface Ll97FileRef {
  id: string;
  /** basename of the BuildingFile `file` URL. */
  filename: string;
  /** The `file` URL (download href). */
  url: string;
}

/** The six typed document M2Ms, one slot list each. */
export interface Ll97FileSlots {
  gfa: Ll97FileRef[];
  ecm321: Ll97FileRef[];
  decarb: Ll97FileRef[];
  siteVisit: Ll97FileRef[];
  altFuel: Ll97FileRef[];
  other: Ll97FileRef[];
}

export interface Ll97Rdp {
  name?: string;
  businessName?: string;
  licenseTypeDisplay?: string;
  licenseNumber?: string;
}

export interface Ll97AttestationObj {
  attestationType?: number;
  status?: number;
  calendarYear?: number;
  signerRelationship?: string | null;
  fileUrl?: string | null;
}

/**
 * FR-1 — a BEAM ticket as serialized by `BeamTicketDisplaySerializer`
 * (`audit/serializers/LL97.py`): the stored enum `ticket_type`, the DOB
 * `ticket_number`, the lifecycle `status` (`BEAMTicketStatus`: 0 OPEN /
 * 1 RESOLVED — `audit/models/base.py:134`) and the staff-only `ticket_url`.
 *
 * `nycBins` / `createdAt` are NOT on that serializer's field list TODAY, so
 * they tolerant-parse to `[]` / `null`; the type declares them so an
 * additive serializer change (the model DOES carry `nyc_bins`) is picked up
 * for free. The HONEST owner/staff display label is derived FE-side via
 * `beamTicketLabel(ticketType)` (FR-8) — never the raw enum name/int.
 */
export interface Ll97BeamTicket {
  id: string;
  ticketNumber: string | null;
  ticketType: number | null;
  ticketUrl: string | null;
  status: number | null;
  nycBins: string[];
  createdAt: string | null;
}

/**
 * FR-1 / FR-5 — a DOB-NOW payment as serialized by
 * `NycDobPaymentSubmissionSerializer` (`fields="__all__"`,
 * `audit/serializers/LL97.py:237`). These are the model's REAL fields
 * (`audit/models/submission.py:33-41`): the confirmation NUMBER lives in
 * `dob_payment_transaction_id` — there is NO `payment_confirmation_number`
 * and NO `payment_type` column. The payment TYPE is DERIVED from that
 * confirmation-number prefix grammar (`97(320|321)…`; see
 * `Ll97Process/paymentConfirmation.ts`) plus the `extension_request`
 * linkage (already serialized) — parsing an invented wire key would
 * tolerant-parse to null forever against real paid rows.
 */
export interface Ll97Payment {
  /**
   * DRF `DecimalField` → arrives as a numeric string ("210.00") or `null`.
   * `null` = honest "confirmation on file — amount pending", NEVER `$0`.
   */
  amount: number | null;
  dobPaymentTransactionId: string | null;
  reimbursementStatus: string | null;
  createdAt: string | null;
  /**
   * DERIVED, not a column: true iff a linked `LL97ExtensionRequest` is
   * serialized on this payment — the reliable "this is the extension fee"
   * signal, independent of the confirmation-number letter block.
   */
  isExtension: boolean;
}

/**
 * FR-1 — the reference slice of a CBL dispute off `cbl_dispute`
 * (`LL97CBLDisputeSerializer`, `fields="__all__"`). `beam_ticket_number` is
 * a real column on `LL97CBLDispute` (`audit/models/LL97.py:558`). The
 * dispute correspondence THREAD is FR-2's job (the `LawSubmissionMessage`
 * cleanup); FR-1 only types the reference the process view needs.
 */
export interface Ll97DisputeRef {
  status: number | null;
  beamTicketNumber: string | null;
  nycBins: string[];
}

export interface Ll97ProcessSubmission {
  id: string;
  status: number;
  article?: string | null;
  pathway?: string | null;
  calendarYear?: number | null;
  deadline?: string | null;
  /** Parsed via the shared `filingContextSchema` (live OR frozen snapshot). */
  filingContext?: FilingContext;
  nycBins: string[];
  nycBbls: string[];
  attestationStatus?: number | null;
  attestationObj?: Ll97AttestationObj | null;
  rdp: Ll97Rdp | null;
  rdpConfirmedForCurrentYear?: boolean | null;
  servicesPaid: string[];
  files: Ll97FileSlots;
  /** Real `beam_tickets` rows; `[]` when none (the honest not-submitted case). */
  beamTickets: Ll97BeamTicket[];
  /** Real city payment (`amount` may be `null` = honest pending). */
  dobPaymentSubmission: Ll97Payment | null;
  dobPaymentSubmission2: Ll97Payment | null;
  /** CBL dispute references (thread is FR-2; this is the typed reference slice). */
  disputes: Ll97DisputeRef[];
}

export interface Ll97Activity {
  id: string;
  /** Backend `AuditActivityType` int (STATUS_CHANGE=1, ECM_321=21, …). */
  activityType: number | string | null;
  description: string;
  createdAt: string | null;
}

export interface Ll97RdpRegistryEntry {
  id: string;
  name?: string;
  businessName?: string;
  licenseTypeDisplay?: string;
  licenseNumber?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Last path segment of a URL/path, query-string stripped. */
export const basename = (path: string): string => {
  const noQuery = path.split(/[?#]/)[0];
  const seg = noQuery.split("/").filter(Boolean).pop();
  return seg ? decodeURIComponent(seg) : noQuery;
};

/** Normalize an ArrayField (ints or strings) to a `string[]`. */
const toStringList = (raw: unknown): string[] => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((v) => (v == null ? "" : String(v)))
    .filter((s) => s.length > 0);
};

/** Map one BuildingFile M2M row → `Ll97FileRef` (drops rows with no file). */
const readFileRef = (raw: unknown): Ll97FileRef | null => {
  if (!isObject(raw)) return null;
  const file = dualKey(raw, "file");
  const id = dualKey(raw, "id");
  if (typeof file !== "string" || file.length === 0) return null;
  return {
    id: id == null ? file : String(id),
    filename: basename(file),
    url: file,
  };
};

/** Map a BuildingFile M2M list → `Ll97FileRef[]`. */
const readFileSlot = (raw: unknown): Ll97FileRef[] => {
  if (!Array.isArray(raw)) return [];
  return raw.map(readFileRef).filter((f): f is Ll97FileRef => f !== null);
};

const readRdp = (raw: unknown): Ll97Rdp | null => {
  if (!isObject(raw)) return null;
  return {
    name: dualKey(raw, "name") as string | undefined,
    businessName: dualKey(raw, "business_name") as string | undefined,
    licenseTypeDisplay: dualKey(raw, "license_type_display") as
      | string
      | undefined,
    licenseNumber: dualKey(raw, "license_number") as string | undefined,
  };
};

/** Coerce a wire value to a finite number, else `null` (tolerant). */
const toNum = (raw: unknown): number | null => {
  if (typeof raw === "number") return Number.isFinite(raw) ? raw : null;
  if (typeof raw === "string" && raw.trim() !== "") {
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }
  return null;
};

/**
 * Parse a DRF `DecimalField` amount: a numeric string ("210.00"), a number,
 * or `null`/blank → `null`. `null` is load-bearing (honest pending, never
 * `$0`) so an absent amount must NEVER collapse to `0`.
 */
const toAmount = (raw: unknown): number | null => {
  if (raw == null) return null;
  if (typeof raw === "number") return Number.isFinite(raw) ? raw : null;
  if (typeof raw === "string") {
    const t = raw.trim();
    if (t === "") return null;
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
  }
  return null;
};

/** Map one serialized `beam_tickets` row → `Ll97BeamTicket` (tolerant). */
const readBeamTicket = (raw: unknown): Ll97BeamTicket | null => {
  if (!isObject(raw)) return null;
  const id = dualKey(raw, "id");
  const ticketType = toNum(dualKey(raw, "ticket_type"));
  const ticketNumberRaw = dualKey(raw, "ticket_number");
  const ticketNumber =
    ticketNumberRaw == null || ticketNumberRaw === ""
      ? null
      : String(ticketNumberRaw);
  // Not renderable without at least an id, a ticket number, or a type token.
  if (id == null && ticketNumber == null && ticketType == null) return null;
  const ticketUrl = dualKey(raw, "ticket_url");
  return {
    id: id != null ? String(id) : (ticketNumber ?? String(ticketType)),
    ticketNumber,
    ticketType,
    ticketUrl:
      typeof ticketUrl === "string" && ticketUrl.length > 0 ? ticketUrl : null,
    status: toNum(dualKey(raw, "status")),
    nycBins: toStringList(dualKey(raw, "nyc_bins")),
    createdAt:
      (dualKey(raw, "created_at") as string | null | undefined) ?? null,
  };
};

/** Map a `beam_tickets` list → `Ll97BeamTicket[]`. */
const readBeamTickets = (raw: unknown): Ll97BeamTicket[] => {
  if (!Array.isArray(raw)) return [];
  return raw.map(readBeamTicket).filter((t): t is Ll97BeamTicket => t !== null);
};

/** Map a serialized `dob_payment_submission[_2]` → `Ll97Payment` (tolerant). */
const readPayment = (raw: unknown): Ll97Payment | null => {
  if (!isObject(raw)) return null;
  const txId = dualKey(raw, "dob_payment_transaction_id");
  const reimb = dualKey(raw, "reimbursement_status");
  return {
    amount: toAmount(dualKey(raw, "amount")),
    dobPaymentTransactionId:
      typeof txId === "string" && txId.length > 0 ? txId : null,
    reimbursementStatus:
      typeof reimb === "string" && reimb.length > 0 ? reimb : null,
    createdAt:
      (dualKey(raw, "created_at") as string | null | undefined) ?? null,
    // TYPE is derived, never a column — an `extension_request` object on the
    // payload is the reliable extension-fee signal.
    isExtension: dualKey(raw, "extension_request") != null,
  };
};

/** Map one serialized `cbl_dispute` row → `Ll97DisputeRef` (tolerant). */
const readDisputeRef = (raw: unknown): Ll97DisputeRef | null => {
  if (!isObject(raw)) return null;
  const btn = dualKey(raw, "beam_ticket_number");
  return {
    status: toNum(dualKey(raw, "status")),
    beamTicketNumber: typeof btn === "string" && btn.length > 0 ? btn : null,
    nycBins: toStringList(dualKey(raw, "nyc_bins")),
  };
};

/** Map a `cbl_dispute` list → `Ll97DisputeRef[]`. */
const readDisputeRefs = (raw: unknown): Ll97DisputeRef[] => {
  if (!Array.isArray(raw)) return [];
  return raw.map(readDisputeRef).filter((d): d is Ll97DisputeRef => d !== null);
};

const readAttestationObj = (raw: unknown): Ll97AttestationObj | null => {
  if (!isObject(raw)) return null;
  return {
    attestationType: dualKey(raw, "attestation_type") as number | undefined,
    status: dualKey(raw, "status") as number | undefined,
    calendarYear: dualKey(raw, "calendar_year") as number | undefined,
    signerRelationship: dualKey(raw, "signer_relationship") as
      | string
      | null
      | undefined,
    fileUrl: dualKey(raw, "file") as string | null | undefined,
  };
};

// ---------------------------------------------------------------------------
// Parsers
// ---------------------------------------------------------------------------

/**
 * Parse the full LL97 submission serializer payload into the declared
 * process-view slice. Returns `null` (never throws) when the required
 * core (`id` + numeric `status`) is missing — additive-contract
 * tolerance, matching `getFilingContext`.
 *
 * `filing_context` is preferred; the frozen `filing_context_snapshot`
 * (terminal cycles) is the fall-back — both share the same shape and
 * ride the shared `filingContextSchema`.
 */
export const parseLl97ProcessSubmission = (
  raw: unknown,
): Ll97ProcessSubmission | null => {
  if (!isObject(raw)) return null;
  const id = dualKey(raw, "id");
  const status = dualKey(raw, "status");
  if (typeof id !== "string" && typeof id !== "number") return null;
  if (typeof status !== "number") return null;

  const fcRaw =
    dualKey(raw, "filing_context") ?? dualKey(raw, "filing_context_snapshot");
  const filingContext = filingContextSchema.parse(fcRaw);

  return {
    id: String(id),
    status,
    article: dualKey(raw, "article") as string | null | undefined,
    pathway: dualKey(raw, "pathway") as string | null | undefined,
    calendarYear: dualKey(raw, "calendar_year") as number | null | undefined,
    deadline: dualKey(raw, "deadline") as string | null | undefined,
    filingContext,
    nycBins: toStringList(dualKey(raw, "nyc_bins")),
    nycBbls: toStringList(dualKey(raw, "nyc_bbls")),
    attestationStatus: dualKey(raw, "attestation_status") as
      | number
      | null
      | undefined,
    attestationObj: readAttestationObj(dualKey(raw, "attestation_obj")),
    rdp: readRdp(dualKey(raw, "rdp")),
    rdpConfirmedForCurrentYear: dualKey(
      raw,
      "rdp_confirmed_for_current_year",
    ) as boolean | null | undefined,
    servicesPaid: toStringList(dualKey(raw, "services_paid")),
    files: {
      gfa: readFileSlot(dualKey(raw, "gfa_files")),
      ecm321: readFileSlot(dualKey(raw, "ecm_321_files")),
      decarb: readFileSlot(dualKey(raw, "decarb_plan_files")),
      siteVisit: readFileSlot(dualKey(raw, "site_visit_audit_files")),
      altFuel: readFileSlot(dualKey(raw, "alternative_fuel_files")),
      other: readFileSlot(dualKey(raw, "other_files")),
    },
    beamTickets: readBeamTickets(dualKey(raw, "beam_tickets")),
    dobPaymentSubmission: readPayment(dualKey(raw, "dob_payment_submission")),
    dobPaymentSubmission2: readPayment(
      dualKey(raw, "dob_payment_submission_2"),
    ),
    disputes: readDisputeRefs(dualKey(raw, "cbl_dispute")),
  };
};

/**
 * Parse the audit-trail rows into the minimal render contract:
 * `{id, activityType, description, createdAt}` ONLY.
 *
 * MANDATORY serializer-leak defense (DESIGN_SPEC §8.4): the LL97
 * audit_trail endpoint serializes `fields="__all__"`, which includes
 * `internal_description` and `extra_data`. Those are EXPLICITLY not
 * carried onto the render contract here — they never reach a component.
 *
 * Sorted ascending by `createdAt` (the trail reads oldest-first).
 */
export const parseLl97AuditTrail = (raw: unknown): Ll97Activity[] => {
  if (!Array.isArray(raw)) return [];
  const rows: Ll97Activity[] = [];
  for (const item of raw) {
    if (!isObject(item)) continue;
    const id = dualKey(item, "id");
    if (id == null) continue;
    rows.push({
      id: String(id),
      activityType:
        (dualKey(item, "activity_type") as
          | number
          | string
          | null
          | undefined) ?? null,
      description: (dualKey(item, "description") as string | undefined) ?? "",
      createdAt:
        (dualKey(item, "created_at") as string | null | undefined) ?? null,
      // internal_description + extra_data are DELIBERATELY OMITTED (leak).
    });
  }
  return rows.sort((a, b) => {
    const at = a.createdAt ?? "";
    const bt = b.createdAt ?? "";
    return at < bt ? -1 : at > bt ? 1 : 0;
  });
};

/**
 * FR-2 — parse the `LawSubmissionMessage` thread wire into `Ll97Message[]`.
 *
 * Tolerant + snake-first (`dualKey`), matching every other parser here: reads
 * the same payload whether it rode the axios camelizer OR a raw `codecGet`
 * path, and DROPS a row that lacks a renderable core (`id`). `extra_data` is
 * read through the whitelisted keys only — the FE never re-widens what the
 * server already narrowed. `visibility` is carried through when present (the
 * staff wire) and simply absent on the owner wire.
 *
 * Order is preserved as served (the BE orders by `created_at`); no client-side
 * re-sort so a same-timestamp thread keeps its insertion order.
 */
export const parseLl97Messages = (raw: unknown): Ll97Message[] => {
  if (!Array.isArray(raw)) return [];
  const rows: Ll97Message[] = [];
  for (const item of raw) {
    if (!isObject(item)) continue;
    const id = dualKey(item, "id");
    if (id == null) continue;
    const party = dualKey(item, "author_party");
    const display = dualKey(item, "author_display");
    const visibility = dualKey(item, "visibility");
    rows.push({
      id: String(id),
      authorParty: typeof party === "string" ? party : "system",
      authorDisplay: typeof display === "string" ? display : "",
      body: (dualKey(item, "body") as string | undefined) ?? "",
      createdAt:
        (dualKey(item, "created_at") as string | null | undefined) ?? null,
      // visibility ships on the STAFF wire only; absent for owners.
      ...(typeof visibility === "string" ? { visibility } : {}),
      extraData: readMessageExtraData(dualKey(item, "extra_data")),
    });
  }
  return rows;
};

/** Read the whitelisted `extra_data` surface off a message row (tolerant). */
const readMessageExtraData = (raw: unknown): Ll97MessageExtraData | null => {
  if (!isObject(raw)) return null;
  const routed = dualKey(raw, "routed_recipients");
  const out: Ll97MessageExtraData = {
    handoff: (dualKey(raw, "handoff") as string | null | undefined) ?? null,
    routedRecipients: Array.isArray(routed) ? toStringList(routed) : null,
    statusFrom:
      (dualKey(raw, "status_from") as string | null | undefined) ?? null,
    statusTo: (dualKey(raw, "status_to") as string | null | undefined) ?? null,
    receivedAt:
      (dualKey(raw, "received_at") as string | null | undefined) ?? null,
  };
  return out;
};

/** Parse the company RDP registry (S2 chooser). */
export const parseRdps = (raw: unknown): Ll97RdpRegistryEntry[] => {
  if (!Array.isArray(raw)) return [];
  const rows: Ll97RdpRegistryEntry[] = [];
  for (const item of raw) {
    if (!isObject(item)) continue;
    const id = dualKey(item, "id");
    if (id == null) continue;
    rows.push({
      id: String(id),
      name: dualKey(item, "name") as string | undefined,
      businessName: dualKey(item, "business_name") as string | undefined,
      licenseTypeDisplay: dualKey(item, "license_type_display") as
        | string
        | undefined,
      licenseNumber: dualKey(item, "license_number") as string | undefined,
    });
  }
  return rows;
};

// ---------------------------------------------------------------------------
// Fetcher (RDP registry — no existing FE hook; Settings calls it inline)
// ---------------------------------------------------------------------------

export const fetchRdps = (): Promise<unknown> =>
  api.get("/audits/rdps/").then((res) => res.data);

// ---------------------------------------------------------------------------
// FR-2 — the `LawSubmissionMessage` thread endpoints (comms wire). Both return
// the reader's VISIBILITY-FILTERED thread as a LIST (owners get `owner_visible`
// rows only — filtered SERVER-side; `staff_internal` never reaches this
// payload). Raw `res.data` is returned; the caller runs `parseLl97Messages`
// (the tolerant-parse-at-the-boundary convention this module already follows).
// ---------------------------------------------------------------------------

/** GET the LL97 submission correspondence thread (S5). Read-only for owners —
 *  owner posting happens on the CBL-dispute thread, never the filing thread. */
export const fetchLl97Messages = (submissionId: string): Promise<unknown> =>
  api.get(`/audits/ll97/${submissionId}/messages/`).then((res) => res.data);

/** GET the CBL-dispute correspondence thread. */
export const fetchLl97DisputeMessages = (
  disputeId: string,
): Promise<unknown> =>
  api
    .get(`/audits/cbl_disputes/${disputeId}/messages/`)
    .then((res) => res.data);

/**
 * POST an owner message onto a CBL dispute. TEXT-ONLY — the body is `{ body }`
 * and nothing else: `party`/`visibility` are FORCED `owner`/`owner_visible`
 * server-side for non-staff (a smuggled value is overwritten, never an error),
 * and a `files` key 400s by design. The service-side resubmit rule flips a
 * status-6 (INFORMATION_REQUESTED) dispute to SUBMITTED_TO_CC on an owner post —
 * so the FE sends NO status field (the old `status=1` PATCH is retired, FR-2).
 * Returns the refreshed thread LIST (the endpoint re-serializes after the post).
 */
export const postLl97DisputeMessage = (
  disputeId: string,
  args: { body: string },
): Promise<unknown> =>
  api
    .post(`/audits/cbl_disputes/${disputeId}/messages/`, { body: args.body })
    .then((res) => res.data);

// ---------------------------------------------------------------------------
// The ONE sanctioned write — `POST …/ecm_321_documentation_complete`
// (`ll97_submission_view.py:600`). GATED OFF by default (see
// `Ll97Process/config.ts` — `LL97_ECM_LIVE_WRITE`); this function exists so
// the plumbing is ready when Leo enables the live write on the demo (with
// the seed prereq `paid=True` + a demo-reset that rewinds 460→444). The
// per-BIN `.xlsx` file rows are attached to the submission through the
// separate file-upload flow BEFORE this completion call; on success the
// server advances status 444→460, runs `prefill_attestation()`, and emits
// the type-21 activity — the UI then re-reads the wire (never fabricates).
// ---------------------------------------------------------------------------

export interface EcmQuestionData {
  questions: Record<string, string>;
  responses: Record<string, string>;
}

export const postEcmDocumentationComplete = (
  submissionId: string,
  questionData: EcmQuestionData,
): Promise<unknown> =>
  api
    .post(
      `/audits/ll97/${submissionId}/ecm_321_documentation_complete/`,
      { question_data: questionData },
      { timeout: 60_000 },
    )
    .then((res) => res.data);
